/* ========================================================================== *\
	IMPORTS
\* ========================================================================== */
import isDate from 'lodash/isDate';

import {
	addMonths,
	removeTime
} from 'js-date-helper';

import Config, { isDateEqual } from '../../01-helpers/utilities/configuration.js';

import NavigatorBase from './navigator.js';
import DateRange from '../../01-helpers/utilities/date-range.js';



/* ========================================================================== *\
	PRIVATE VARIABLES
\* ========================================================================== */
const
	propertyKey = {
		currentDate: 'currentDate',
		propertyManager: Symbol('propertyManager'),
		rangeMinMax: Symbol('rangeMinMax')
	};



/* ========================================================================== *\
	PRIVATE METHODS
\* ========================================================================== */
/**
 * @this DateNavigator
 */
function checkAllowedDirections() {
	this.canGoBack = this.navigationRange.isDateInRange(addMonths(this.currentDate, -this.stepSize));
	this.canGoForward = this.navigationRange.isDateInRange(addMonths(this.currentDate, this.stepSize));
}

/**
 *
 * @param {*} key
 */
function getOption(key) {
	return this[propertyKey.propertyManager].getOption(key);
}

/**
 *
 * @param {*} key
 * @param {*} value
 */
function setOption(key, value) {
	return this[propertyKey.propertyManager].setOption(key, value);
}



/* ========================================================================== *\
	SETUP
\* ========================================================================== */
/**
 * @this DateNavigator
 */
function setup() {
	setupProperties.call(this);
}

/**
 * @this DateNavigator
 */
function setupProperties() {
	this[propertyKey.propertyManager] = new Config({
		[propertyKey.currentDate]: {
			compare: isDateEqual,
			transform: removeTime,
			validator: isDate
		}
	});

	this[propertyKey.rangeMinMax] = new DateRange({
		allowInfiniteRange: true
	});
}



/* ========================================================================== *\
	PUBLIC API
\* ========================================================================== */
class DateNavigator extends NavigatorBase {
	/* ====================================================================== *\
		CONSTRUCTOR
	\* ====================================================================== */
	constructor(baseElement, options) {
		super(baseElement);

		setup.call(this);
	}



	/* ====================================================================== *\
		INSTANCE PROPERTIES
	\* ====================================================================== */

	/* ---------------------------------- *\
		currentDate
	\* ---------------------------------- */
	/**
	 * @type {Date}
	 *
	 * @memberof DateNavigator
	 */
	get currentDate() {
		return getOption.call(this, propertyKey.currentDate);
	}
	set currentDate(value) {
		if (!setOption.call(this, propertyKey.currentDate, value)) {
			return;
		}
		checkAllowedDirections.call(this);
		this._dispatchInstanceEvent(NavigatorBase.events.positionChanged);
	}

	/* ---------------------------------- *\
		navigationRange
	\* ---------------------------------- */
	/**
	 * @type {DateRange}
	 *
	 * @readonly
	 * @memberof DateNavigator
	 */
	get navigationRange() {
		return this[propertyKey.rangeMinMax];
	}

	/* ---------------------------------- *\
		navigationMode
	\* ---------------------------------- */

	/* ---------------------------------- *\
		stepSize
	\* ---------------------------------- */
	/**
	 * @type {number}
	 *
	 * @readonly
	 * @memberof DateNavigator
	 */
	get stepSize() {
		return 1;
	}



	/* ====================================================================== *\
		OVERRIDES
	\* ====================================================================== */
	/**
	 *
	 * @param {import('../04-components/navigator/navigator.js/index.js.js').NavigationDirection} direction
	 *
	 * @override
	 */
	_preprocessNavigate(direction) {
		const
			stepSize = this.stepSize,
			delta = (direction === NavigatorBase.direction.forward)
				? stepSize
				: -stepSize;

		setOption.call(this, propertyKey.currentDate, addMonths(this.currentDate, delta));

		checkAllowedDirections.call(this);
	}
}



/* ========================================================================== *\
	EXPORTS
\* ========================================================================== */
export default DateNavigator;
